/**
 * Marquer, dans un rjBuild, ce que l'éditeur ne pourra pas modifier : le contenu
 * qui vient du SERVEUR.
 *
 * Un bloc dynamique (liste Elasticsearch, compteur de résultats) déclare une
 * `additionalDataSource` et rend ce qu'elle rapporte. Ce texte n'appartient pas
 * au bloc : il n'existe nulle part dans ses données, il est recalculé à chaque
 * affichage. Cliquer dessus en mode édition n'ouvre rien — et c'est justement le
 * problème, parce que RIEN ne le disait : l'éditeur croyait à une panne. On
 * grise donc ces régions et on leur attache un message (cf. `index.css`).
 *
 * Trois règles, dans cet ordre, et l'ordre EST la garantie :
 *
 *  1. Une région qui contient un champ éditable n'est jamais marquée — on
 *     descend dedans. Sans cette règle, le titre modifiable d'un widget se
 *     retrouverait grisé sous le voile de la liste qui le suit : on aurait
 *     retiré une affordance réelle pour en expliquer une absente.
 *  2. On marque le nœud le PLUS HAUT qui ne parle que du serveur, puis on
 *     s'arrête : un voile par région, pas un par ligne de la liste.
 *  3. On ne marque qu'une BALISE HTML. Un `Switch` ou un composant reactive-json
 *     ne rend pas d'élément propre : l'attribut n'atterrirait nulle part. C'est
 *     alors sa boîte parente qui porte le voile.
 *
 * Appelé APRÈS `expandEditable`, dont la sortie porte déjà `data-builder-field` :
 * « contient un champ éditable » se lit alors sur l'arbre, sans réinterpréter la
 * grammaire des déclarations `editable:`.
 */

const MESSAGE = 'Contenu dynamique : il est chargé depuis le site et se met à jour tout seul. Il ne se modifie pas ici.'

const ATTR = 'data-builder-dynamic'

/** Une balise HTML porte un nom en minuscules ; un composant, une majuscule. */
const isHtmlTag = (type) => typeof type === 'string' && /^[a-z][a-z0-9-]*$/.test(type)

/**
 * Les racines de données peuplées par le serveur, telles qu'un chemin
 * reactive-json les nomme : `path: ~~.esResult` → le jeton `~~.esResult`.
 *
 * Le chemin est gardé ENTIER, jamais réduit à sa dernière clé : deux blocs
 * peuvent nommer `results` à des profondeurs différentes.
 */
export const dynamicRoots = (rjBuild) => {
  const sources = rjBuild?.additionalDataSource
  if (!Array.isArray(sources)) return []
  return sources
    .map((src) => (typeof src?.path === 'string' ? src.path.trim() : ''))
    .filter((path) => path.startsWith('~'))
}

const asJson = (node) => {
  try { return JSON.stringify(node) ?? '' }
  catch { return '' }
}

/**
 * Le sous-arbre cite-t-il l'une de ces racines ?
 *
 * La comparaison est ancrée sur une FRONTIÈRE de chemin. Un simple `includes`
 * fait passer `~~.esResultatsChoisis` — une donnée du bloc, parfaitement
 * modifiable — pour la racine `~~.esResult`, et voile alors un texte que
 * l'éditeur pouvait changer. Après la racine il faut donc un caractère qui ne
 * prolonge pas la clé : un point, un guillemet, une fin de chaîne.
 */
const rootPattern = (root) => new RegExp(`${root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w-])`)

const referencesRoot = (node, roots) => {
  const json = asJson(node)
  return roots.some((root) => rootPattern(root).test(json))
}

/**
 * Les types qui se MANIPULENT. Un contrôle dont les options viennent du serveur
 * — une facette, une barre de filtres de widget, un champ de recherche — cite la
 * même racine dynamique que le contenu, et le voile lui irait très mal : ces
 * commandes-là sont justement ce que l’éditeur règle dans le canevas (les
 * `dash-filters` ne s’affichent QUE en mode édition). On descend donc dedans au
 * lieu de les éteindre.
 *
 * Mesuré : sans cette règle, `DashboardFilterBar` et les blocs à facettes
 * partaient entièrement grisés, avec un message leur reprochant de ne pas être
 * modifiables.
 */
const INTERACTIVE_TYPES = new Set([
  "button", "select", "input", "textarea", "form", "label", "a",
  "FacetList", "RangeFacet", "SearchField", "Pagination", "SortControl",
  "ActiveFilterChips", "FloatingDropdown", "Carousel",
])

const hasInteractive = (node) => {
  if (Array.isArray(node)) return node.some(hasInteractive)
  if (!node || typeof node !== "object") return false
  const type = node.type
  if (typeof type === "string" && (INTERACTIVE_TYPES.has(type) || type.endsWith("Field"))) return true
  return Object.values(node).some((v) => (v && typeof v === "object" ? hasInteractive(v) : false))
}

const hasEditable = (node) => {
  const json = asJson(node)
  return json.includes('data-builder-field') || json.includes('"editable"')
}

/**
 * Pose l'attribut sur les régions dynamiques de `rjBuild`.
 *
 * Mute l'arbre reçu (il sort de `expandEditable`, qui vient de le construire
 * pour cet usage) et le renvoie. Un build sans `additionalDataSource` ressort
 * inchangé, sans parcours : la très grande majorité des blocs ne paie rien.
 */
export const markDynamicRegions = (rjBuild) => {
  const roots = dynamicRoots(rjBuild)
  if (!rjBuild || roots.length === 0) return rjBuild

  const walk = (node) => {
    if (Array.isArray(node)) { node.forEach(walk); return }
    if (!node || typeof node !== 'object') return

    // Règles 1 à 3, dans l'ordre : rien de modifiable ici, ça parle du serveur,
    // et c'est une vraie balise → le voile est posé et la descente s'arrête.
    if (!hasEditable(node) && !hasInteractive(node) && referencesRoot(node, roots) && isHtmlTag(node.type)) {
      node.attributes = { ...(node.attributes ?? {}), [ATTR]: MESSAGE }
      return
    }

    for (const value of Object.values(node)) {
      if (value && typeof value === 'object') walk(value)
    }
  }

  walk(rjBuild.renderView)
  return rjBuild
}
